import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Plus, Buildings } from '@phosphor-icons/react';
import { apiPath } from '../../config/apiBase';
import { notifySuccess, notifyError } from '../../lib/notify';
import RowActionsMenu from '../../components/RowActionsMenu';

const API = apiPath('/api/companies');

function authHeaders() {
  const token = localStorage.getItem('token');
  return { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
}

const EMPTY_FORM = { name: '', description: '' };

/** Simple Company master-data panel — list/create/edit/deactivate, opened from Product Management. */
function CompaniesPanel({ onClose, onChanged }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'create' | company object
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(API, authHeaders());
      setCompanies(r.data);
    } catch (e) {
      console.error('Failed to fetch companies:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openEdit = (company) => {
    setError(null);
    setEditing(company || 'create');
    setForm(company ? { name: company.name, description: company.description || '' } : EMPTY_FORM);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (editing === 'create') {
        await axios.post(API, form, authHeaders());
        notifySuccess('Company created');
      } else {
        await axios.patch(`${API}/${editing.company_id}`, form, authHeaders());
        notifySuccess('Company updated');
      }
      setEditing(null);
      await refresh();
      onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to save company';
      setError(detail);
      notifyError(detail);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (company) => {
    try {
      await axios.patch(`${API}/${company.company_id}`, { active: !company.active }, authHeaders());
      await refresh();
      onChanged?.();
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to update company');
    }
  };

  const remove = async (company) => {
    if (!window.confirm(`Delete company "${company.name}"?`)) return;
    try {
      await axios.delete(`${API}/${company.company_id}`, authHeaders());
      notifySuccess('Company deleted');
      await refresh();
      onChanged?.();
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to delete company');
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '10px',
    }}>
      <div style={{ background: 'white', padding: '24px', width: '100%', maxWidth: '640px', maxHeight: '85vh', overflowY: 'auto', borderRadius: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Buildings size={20} weight="bold" />
            <h2 style={{ fontSize: '15px', fontWeight: '600', margin: 0 }}>Companies</h2>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <X size={22} />
          </button>
        </div>

        {!editing && (
          <>
            <button type="button" onClick={() => openEdit(null)} className="tk-btn tk-btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: '12px' }}>
              <Plus size={16} weight="bold" /> Add company
            </button>
            {loading ? (
              <p style={{ fontSize: '12px', color: '#6B7280' }}>Loading…</p>
            ) : companies.length === 0 ? (
              <p style={{ fontSize: '12px', color: '#6B7280' }}>No companies yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {companies.map((c) => (
                  <div key={c.company_id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: '8px',
                  }}>
                    <div>
                      <p style={{ fontSize: '13px', fontWeight: '600', margin: 0 }}>
                        {c.name} {!c.active && <span style={{ fontSize: '11px', color: '#9CA3AF' }}>inactive</span>}
                      </p>
                      {c.description && <p style={{ fontSize: '12px', color: '#6B7280', margin: '2px 0 0' }}>{c.description}</p>}
                    </div>
                    <RowActionsMenu actions={[
                      { label: 'Edit', onClick: () => openEdit(c) },
                      { label: c.active ? 'Deactivate' : 'Activate', onClick: () => toggleActive(c) },
                      { label: 'Delete', onClick: () => remove(c), danger: true },
                    ]} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {editing && (
          <form onSubmit={save}>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Name *</label>
              <input type="text" value={form.name} required className="input-field"
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Description</label>
              <textarea value={form.description} className="input-field" rows={2}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            {error && <p style={{ color: '#B91C1C', fontSize: '12px', marginBottom: '8px' }}>{error}</p>}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="submit" className="tk-btn tk-btn-primary" style={{ flex: 1 }} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="tk-btn tk-btn-secondary" style={{ flex: 1 }}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default CompaniesPanel;
